// Valori canonici dell'anagrafica clienti (allineati ai valori reali in DB:
// clienti.tipo_cliente ∈ privato | azienda | libero_professionista | ente_associazione).
// Usato sia dai modali di fatturazione sia dal completamento profilo ("welcome").

// Enum canonico (allineato alla console, validato da neoflux-api clienteSchema)
export const TIPO_CLIENTE = [
  { value: 'privato', label: 'Privato' },
  { value: 'azienda', label: 'Azienda' },
  { value: 'libero_professionista', label: 'Libero professionista' },
  { value: 'ente_pubblico', label: 'Pubblica amministrazione' },
  { value: 'ente_associazione', label: 'Ente o associazione' },
  { value: 'altro', label: 'Altro' },
];

// Persone fisiche: CF personale (16 caratteri) → colonna codice_fiscale.
// Gli altri: CF organizzazione (11 cifre) → colonna codice_fiscale_org.
export const PERSONA_FISICA = ['privato', 'libero_professionista'];
export const isPersonaFisica = (t) => PERSONA_FISICA.includes(t);
// true se il tipo richiede i dati fiscali (ragione sociale, P.IVA, SDI, PEC).
export const isBusinessTipo = (t) => t && t !== 'privato';

// Stati membri UE (codici ISO alpha-2) — usati per il regime IVA (reverse charge).
export const EU_CODES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];

export const ALIQUOTA_IVA = 22;

// Logica fiscale: da (nazione, tipo) deriva quali campi chiedere e il regime IVA.
// region: 'IT' | 'EU' | 'EXTRA'
// regime: 'imponibile' (IVA) | 'reverse_charge' (UE B2B) | 'fuori_campo' (extra-UE)
export function fiscalFor(nazione, tipo) {
  const cc = (nazione || 'IT').toUpperCase();
  const region = cc === 'IT' ? 'IT' : (EU_CODES.includes(cc) ? 'EU' : 'EXTRA');
  const business = isBusinessTipo(tipo);

  const personaFisica = isPersonaFisica(tipo);
  // Campi richiesti/mostrati
  const fields = {
    ragioneSociale: business,
    // CF personale (16) per persone fisiche IT; CF org (11) per enti/aziende IT
    codiceFiscale: region === 'IT' && personaFisica,
    codiceFiscaleReq: region === 'IT' && tipo === 'privato',
    codiceFiscaleOrg: region === 'IT' && !personaFisica,
    partitaIva: region === 'IT' && business,  // P.IVA nazionale: Italia B2B
    partitaIvaComunitaria: region === 'EU' && business, // VAT UE (VIES)
    sdiPec: region === 'IT' && business,      // SDI o PEC per fattura elettronica IT
    sdiLen: tipo === 'ente_pubblico' ? 6 : 7, // PA usa codice IPA a 6
    // Identificativo fiscale estero (facoltativo): per QUALSIASI cliente non-IT,
    // tranne il business UE che usa già la P.IVA comunitaria (VIES).
    taxId: region === 'EXTRA' || (region === 'EU' && !business),
  };

  // regime + dati per la fattura elettronica (natura/dicitura) e adempimenti.
  // Riferimenti: art. 7-ter DPR 633/1972; reg. UE 282/2011 mod. 2017/2459 (localizzazione).
  let regime, aliquota, nota;
  let natura = null;        // codice natura fattura elettronica (es. N2.1)
  let dicitura = null;      // dicitura obbligatoria in fattura
  let viesRequired = false; // obbligo di verifica iscrizione VIES della P.IVA
  let proofRequired = false; // conservare ≥2 prove di localizzazione del cliente
  if (region === 'IT') {
    regime = 'imponibile'; aliquota = ALIQUOTA_IVA;
    nota = `IVA ${ALIQUOTA_IVA}% applicata.`;
  } else if (region === 'EU' && business) {
    regime = 'reverse_charge'; aliquota = 0; viesRequired = true; proofRequired = true;
    natura = 'N2.1';
    dicitura = 'Inversione contabile — art. 7-ter DPR 633/1972';
    nota = 'UE B2B: operazione non soggetta per territorialità (art. 7-ter). Obbligatoria la verifica dell’iscrizione al VIES della P.IVA; il cliente dev’essere soggetto passivo. Natura N2.1, dicitura «inversione contabile». Annotare nei registri IVA.';
  } else if (region === 'EU') {
    regime = 'imponibile'; aliquota = ALIQUOTA_IVA; proofRequired = true;
    nota = `Cliente privato UE: IVA del paese del cliente tramite regime OSS (servizi digitali). Raccogliere e conservare almeno 2 prove di localizzazione non contraddittorie.`;
  } else if (business) {
    regime = 'fuori_campo'; aliquota = 0; proofRequired = true;
    natura = 'N2.1';
    dicitura = 'Operazione non soggetta — art. 7-ter DPR 633/1972';
    nota = 'Extra-UE B2B: operazione non soggetta (art. 7-ter). Verificare che il cliente sia soggetto passivo d’imposta. Natura N2.1. Obbligo di fattura, annotazione nei registri IVA e conservazione delle prove.';
  } else {
    regime = 'fuori_campo'; aliquota = 0; proofRequired = true;
    natura = 'N2.1';
    dicitura = 'Operazione non soggetta — art. 7-ter DPR 633/1972';
    nota = 'Extra-UE B2C: servizio fuori campo IVA in Italia. Consigliata la conservazione di ≥2 prove di localizzazione del cliente.';
  }

  return { region, business, fields, regime, aliquota, natura, dicitura, viesRequired, proofRequired, nota };
}

// Lista nazioni (ISO 3166-1 alpha-2, nomi in italiano). Italia in cima, poi alfabetico.
export const PAESI = [
  { code: 'IT', name: 'Italia' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua e Barbuda' },
  { code: 'SA', name: 'Arabia Saudita' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaigian' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrein' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BE', name: 'Belgio' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BY', name: 'Bielorussia' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia ed Erzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brasile' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambogia' },
  { code: 'CM', name: 'Camerun' },
  { code: 'CA', name: 'Canada' },
  { code: 'CV', name: 'Capo Verde' },
  { code: 'TD', name: 'Ciad' },
  { code: 'CL', name: 'Cile' },
  { code: 'CN', name: 'Cina' },
  { code: 'CY', name: 'Cipro' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comore' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (Rep. Dem.)' },
  { code: 'KP', name: 'Corea del Nord' },
  { code: 'KR', name: 'Corea del Sud' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: "Costa d'Avorio" },
  { code: 'HR', name: 'Croazia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'DK', name: 'Danimarca' },
  { code: 'DM', name: 'Dominica' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egitto' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'AE', name: 'Emirati Arabi Uniti' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Etiopia' },
  { code: 'FJ', name: 'Figi' },
  { code: 'PH', name: 'Filippine' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'FR', name: 'Francia' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germania' },
  { code: 'GH', name: 'Ghana' },
  { code: 'JM', name: 'Giamaica' },
  { code: 'JP', name: 'Giappone' },
  { code: 'GI', name: 'Gibilterra' },
  { code: 'DJ', name: 'Gibuti' },
  { code: 'JO', name: 'Giordania' },
  { code: 'GR', name: 'Grecia' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GQ', name: 'Guinea Equatoriale' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Irlanda' },
  { code: 'IS', name: 'Islanda' },
  { code: 'IL', name: 'Israele' },
  { code: 'KZ', name: 'Kazakistan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kirghizistan' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'LA', name: 'Laos' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LV', name: 'Lettonia' },
  { code: 'LB', name: 'Libano' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lituania' },
  { code: 'LU', name: 'Lussemburgo' },
  { code: 'MK', name: 'Macedonia del Nord' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldive' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MA', name: 'Marocco' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Messico' },
  { code: 'MD', name: 'Moldavia' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MZ', name: 'Mozambico' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NO', name: 'Norvegia' },
  { code: 'NZ', name: 'Nuova Zelanda' },
  { code: 'OM', name: 'Oman' },
  { code: 'NL', name: 'Paesi Bassi' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua Nuova Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Perù' },
  { code: 'PL', name: 'Polonia' },
  { code: 'PT', name: 'Portogallo' },
  { code: 'QA', name: 'Qatar' },
  { code: 'GB', name: 'Regno Unito' },
  { code: 'CZ', name: 'Repubblica Ceca' },
  { code: 'CF', name: 'Repubblica Centrafricana' },
  { code: 'DO', name: 'Repubblica Dominicana' },
  { code: 'RO', name: 'Romania' },
  { code: 'RW', name: 'Ruanda' },
  { code: 'RU', name: 'Russia' },
  { code: 'KN', name: 'Saint Kitts e Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent e Grenadine' },
  { code: 'SB', name: 'Salomone' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'São Tomé e Príncipe' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SY', name: 'Siria' },
  { code: 'SK', name: 'Slovacchia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ES', name: 'Spagna' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'US', name: 'Stati Uniti' },
  { code: 'ZA', name: 'Sudafrica' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SS', name: 'Sudan del Sud' },
  { code: 'SE', name: 'Svezia' },
  { code: 'CH', name: 'Svizzera' },
  { code: 'TJ', name: 'Tagikistan' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailandia' },
  { code: 'TL', name: 'Timor Est' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad e Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turchia' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UA', name: 'Ucraina' },
  { code: 'UG', name: 'Uganda' },
  { code: 'HU', name: 'Ungheria' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vaticano' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];
